import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Home,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Plus,
  History,
  DollarSign,
  AlertCircle,
  X,
  Check
} from 'lucide-react';
import { getCurrentBodyCorporateId, getCurrentUserId, supabase, Account, Flat, Owner, Transaction } from '../lib/supabase';
import toast from 'react-hot-toast';
import LevyService, { getTransactionTypeLabel } from '../services/LevyService';
import AuditService from '../services/AuditService';
import { formatDate, getLocalDateString, toLevyPeriodDate } from '../lib/dateUtils';
import { calculateBalanceFromTransactions, getBalanceColorClass, getBalanceStatus } from '../lib/balanceUtils';

interface AccountDetailsRecord extends Account {
  flat?: Flat;
  owners?: Owner[];
  balance?: number;
  transactions?: Transaction[];
}

const AccountDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [account, setAccount] = useState<AccountDetailsRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddLevy, setShowAddLevy] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [transactionForm, setTransactionForm] = useState({
    description: '',
    amount: '',
    transaction_date: getLocalDateString(),
    due_date: '',
    reference_number: '',
    notes: '',
transaction_type: 'monthly_levy' as Transaction['transaction_type'],
    levy_period: getLocalDateString().slice(0, 7),
  });

  const [bodyCorporateId, setBodyCorporateId] = useState<string | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      const idForCorp = await getCurrentBodyCorporateId();
      setBodyCorporateId(idForCorp);
      if (id) fetchAccountDetails(idForCorp);
    };

    load();
  }, [id]);

  const fetchAccountDetails = async (activeBodyCorporateId: string | null) => {
    if (!id) return;
    setLoading(true);

    if (!activeBodyCorporateId) {
      setLoading(false);
      toast.error('No body corporate is configured for this user.');
      return;
    }

    try {
      // Get account
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', id)
        .single();

      if (accountError) throw accountError;

      // Get flat
      const { data: flatData } = await supabase
        .from('flats')
        .select('*')
        .eq('id', accountData.flat_id)
        .single();

      // Get account owners
      const { data: accountOwnersData } = await supabase
        .from('account_owners')
        .select('owner_id')
        .eq('account_id', id)
        .eq('is_primary', true)
        .single();

      let owners: Owner[] = [];
      if (accountOwnersData) {
        const { data: ownerData } = await supabase
          .from('owners')
          .select('*')
          .eq('id', accountOwnersData.owner_id)
          .single();
        if (ownerData) owners = [ownerData];
      }

      // Get transactions
      const { data: transactionsData } = await supabase
        .from('transactions')
        .select('*')
        .eq('account_id', id)
        .eq('status', 'posted')
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false });

      // Calculate balance from the ledger with the shared formula.
      const balance = calculateBalanceFromTransactions(transactionsData || []);

      setAccount({
        ...accountData,
        flat: flatData || undefined,
        owners,
        balance,
        transactions: transactionsData || [],
      });
    } catch (error) {
      console.error(error);
      toast.error('Error fetching account details');
    }
    setLoading(false);
  };

  const handleAddTransaction = async (e: React.FormEvent, type: 'levy' | 'payment' | 'charge') => {
    e.preventDefault();
    if (!account) return;

    const amount = parseFloat(transactionForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const selectedType = type === 'payment' ? 'payment' : transactionForm.transaction_type;
    const isPayment = type === 'payment' || selectedType === 'payment';
    const isCreditAdjustment = selectedType === 'credit_adjustment';
    const isDebit = !isPayment && !isCreditAdjustment;
    const debitAmount = isDebit ? amount : 0;
    const creditAmount = isPayment || isCreditAdjustment ? amount : 0;

    const referenceNumber = transactionForm.reference_number || `TRX-${Date.now().toString().slice(-6)}`;
    let error: any = null;
    let penaltiesApplied = 0;
    let paymentTransactionId: string | undefined;

    if (type === 'payment') {
      if (!bodyCorporateId) {
        toast.error('No body corporate is configured for this payment.');
        return;
      }

      try {
        const result = await new LevyService(bodyCorporateId).recordPayment({
          accountId: account.id,
          transactionDate: transactionForm.transaction_date,
          description: transactionForm.description,
          referenceNumber,
          amount,
          notes: transactionForm.notes,
        });
        penaltiesApplied = result.penaltiesApplied;
        paymentTransactionId = result.paymentTransactionId;
      } catch (paymentError) {
        error = paymentError;
      }
    } else {
      const currentUserId = await getCurrentUserId();
      const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .insert({
          body_corporate_id: bodyCorporateId || null,
          account_id: account.id,
          transaction_date: transactionForm.transaction_date,
          transaction_type: selectedType,
          description: transactionForm.description,
          reference_number: referenceNumber,
          due_date: transactionForm.due_date || (type === 'levy' ? `${transactionForm.transaction_date.slice(0, 7)}-07` : null),
          levy_period: type === 'levy' ? toLevyPeriodDate(transactionForm.levy_period || transactionForm.transaction_date) : null,
          debit_amount: debitAmount,
          credit_amount: creditAmount,
          notes: transactionForm.notes,
          status: 'posted',
          created_by: currentUserId,
        })
        .select()
        .single();
      error = transactionError;
      if (!transactionError && bodyCorporateId && currentUserId && transaction) {
        await new AuditService(bodyCorporateId, currentUserId).logAction('CREATE', 'transaction', transaction.id, undefined, transaction);
      }
    }

    if (error) {
      toast.error(`Error adding ${type}`);
      console.error(error);
    } else {
      if (type === 'payment' && bodyCorporateId && paymentTransactionId) {
        const userId = await getCurrentUserId();
        if (userId) await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'transaction', paymentTransactionId, undefined, {
          account_id: account.id, transaction_type: 'payment', amount, reference_number: referenceNumber,
        });
      }
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} added successfully${penaltiesApplied ? `; ${penaltiesApplied} late-payment ${penaltiesApplied === 1 ? 'penalty was' : 'penalties were'} added` : ''}`);
      setShowAddLevy(false);
      setShowAddPayment(false);
      setShowAddCharge(false);
      setTransactionForm({
        description: '',
        amount: '',
        transaction_date: getLocalDateString(),
        due_date: '',
        reference_number: '',
        notes: '',
        transaction_type: 'monthly_levy',
        levy_period: getLocalDateString().slice(0, 7),
      });
      fetchAccountDetails(bodyCorporateId);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading account details...</div>;
  }

  if (!account) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="mx-auto text-gray-400" size={48} />
        <p className="mt-4 text-gray-500">Account not found</p>
      </div>
    );
  }

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'payment': return 'bg-green-100 text-green-800';
      case 'monthly_levy':
      case 'quarterly_levy':
      case 'special_levy':
      case 'maintenance_levy': return 'bg-blue-100 text-blue-800';
      case 'penalty':
      case 'interest': return 'bg-red-100 text-red-800';
      case 'other_charge': return 'bg-purple-100 text-purple-800';
      case 'credit_adjustment': return 'bg-teal-100 text-teal-800';
      case 'reversal': return 'bg-orange-100 text-orange-800';
      case 'opening_balance': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return `R ${amount.toFixed(2)}`;
  };

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={() => navigate('/accounts')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={20} />
        Back to Accounts
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <span>{account.account_number}</span>
            <span className={`text-sm font-medium px-2 py-1 rounded-full ${
              account.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {account.status}
            </span>
          </h1>
          <p className="text-gray-500">Flat {account.flat?.flat_number}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowAddLevy(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Add Levy
          </button>
          <button
            onClick={() => setShowAddPayment(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Add Payment
          </button>
          <button            onClick={() => navigate(`/statements?account=${account.id}`)}
            className="btn-secondary flex items-center gap-2"
          >
            <FileText size={18} />
            Statement
          </button>
        </div>
      </div>

      {/* Balance Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Current Balance</p>
            <p className={`text-3xl font-bold ${getBalanceColorClass(account.balance || 0) === 'text-green-600' ? 'text-green-600' : getBalanceColorClass(account.balance || 0) === 'text-red-600' ? 'text-red-600' : 'text-gray-900'}`}>
              {formatCurrency(account.balance || 0)}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Status</p>
              <p className={`font-medium ${getBalanceColorClass(account.balance || 0)}`}>{getBalanceStatus(account.balance || 0)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Transactions</p>
              <p className="font-medium">{account.transactions?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Owner & Flat Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-medium flex items-center gap-2 mb-3">
            <User size={18} className="text-gray-400" />
            Owner Details
          </h3>
          {account.owners && account.owners.length > 0 ? (
            <div className="space-y-2">
              <p className="font-medium">{account.owners[0].full_name}</p>
              {account.owners[0].email && (
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Mail size={14} className="text-gray-400" />
                  {account.owners[0].email}
                </p>
              )}
              {account.owners[0].telephone && (
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Phone size={14} className="text-gray-400" />
                  {account.owners[0].telephone}
                </p>
              )}
              {account.owners[0].postal_address && (
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <MapPin size={14} className="text-gray-400" />
                  {account.owners[0].postal_address}
                </p>
              )}
            </div>
          ) : (
            <p className="text-gray-500">No owner assigned</p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-medium flex items-center gap-2 mb-3">
            <Home size={18} className="text-gray-400" />
            Flat Details
          </h3>
          {account.flat ? (
            <div className="space-y-2">
              <p className="font-medium">{account.flat.flat_number}</p>
              {account.flat.physical_address && (
                <p className="text-sm text-gray-600">{account.flat.physical_address}</p>
              )}
              {account.flat.participation_quota != null && (
                <p className="text-sm text-gray-600">Participation quota: {account.flat.participation_quota}%</p>
              )}
              <p className="text-sm text-gray-600">
                Status: <span className="capitalize">{account.flat.status}</span>
              </p>
            </div>
          ) : (
            <p className="text-gray-500">No flat assigned</p>
          )}
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-medium flex items-center gap-2">
            <History size={18} className="text-gray-400" />
            Transaction History
          </h3>
          <span className="text-sm text-gray-500">
            {account.transactions?.length || 0} transactions
          </span>
        </div>

        {account.transactions && account.transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {account.transactions.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">
                      {formatDate(t.transaction_date)}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getTransactionTypeColor(t.transaction_type)}`}>
                        {getTransactionTypeLabel(t.transaction_type)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">{t.description}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{t.reference_number || '-'}</td>
                    <td className="px-4 py-2 text-sm text-right text-red-600">
                      {t.debit_amount > 0 ? formatCurrency(t.debit_amount) : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-green-600">
                      {t.credit_amount > 0 ? formatCurrency(t.credit_amount) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No transactions recorded yet
          </div>
        )}
      </div>

      {/* Add Levy Modal */}
      {showAddLevy && (
        <TransactionModal
          title="Add Levy"
          type="levy"
          formData={transactionForm}
          setFormData={setTransactionForm}
          onSubmit={(e) => handleAddTransaction(e, 'levy')}
          onClose={() => setShowAddLevy(false)}
        />
      )}

      {/* Add Payment Modal */}
      {showAddPayment && (
        <TransactionModal
          title="Add Payment"
          type="payment"
          formData={transactionForm}
          setFormData={setTransactionForm}
          onSubmit={(e) => handleAddTransaction(e, 'payment')}
          onClose={() => setShowAddPayment(false)}
        />
      )}

      {/* Add Charge Modal */}
      {showAddCharge && (
        <TransactionModal
          title="Add Charge"
          type="charge"
          formData={transactionForm}
          setFormData={setTransactionForm}
          onSubmit={(e) => handleAddTransaction(e, 'charge')}
          onClose={() => setShowAddCharge(false)}
        />
      )}
    </div>
  );
};

// Transaction Modal Component
interface TransactionModalProps {
  title: string;
  type: 'levy' | 'payment' | 'charge';
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

const TransactionModal: React.FC<TransactionModalProps> = ({
  title,
  type,
  formData,
  setFormData,
  onSubmit,
  onClose,
}) => {
  const isDebit = type === 'levy' || type === 'charge';
  const amountLabel = type === 'payment' ? 'Amount Received' : 'Amount';
  const levyOptions = [
    { value: 'monthly_levy', label: 'Monthly Levy' },
    { value: 'quarterly_levy', label: 'Quarterly Levy' },
    { value: 'special_levy', label: 'Special Levy' },
    { value: 'maintenance_levy', label: 'Maintenance Levy' },
  ];

  const chargeOptions = [
    { value: 'interest', label: 'Interest' },
    { value: 'administrative_charge', label: 'Administrative Charge' },
    { value: 'repair_charge', label: 'Repairs / Approved Charge' },
    { value: 'other_charge', label: 'Other Approved Charge' },
    { value: 'miscellaneous_charge', label: 'Miscellaneous Charge' },
    { value: 'credit_adjustment', label: 'Credit Adjustment' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div className="space-y-4">
            {type === 'levy' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Levy Type
                </label>
                <select
                  value={formData.transaction_type}
                  onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {levyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {type === 'charge' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Charge Type
                </label>
                <select
                  value={formData.transaction_type || 'other_charge'}
                  onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {chargeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <input
                type="text"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={type === 'levy' ? 'Monthly Levy - September 2026' : 
                             type === 'payment' ? 'Payment received' : 'Special levy, repair, interest, penalty, adjustment or administrative charge'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {amountLabel} *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                required
                value={formData.transaction_date}
                onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {type === 'levy' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Levy Period / Month
                </label>
                <input
                  type="month"
                  value={formData.levy_period || formData.transaction_date.slice(0, 7)}
                  onChange={(e) => setFormData({ ...formData, levy_period: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            {isDebit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Default: 7th of the month</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reference Number
              </label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="Auto-generated if left blank"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                placeholder="Additional notes..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary">
              Cancel
            </button>
            <button type="submit" className="flex-1 btn-primary flex items-center justify-center gap-2">
              <Check size={20} />
              Add {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountDetails;
